import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X, Edit, Trash2, Globe } from 'lucide-react';
import { Network, Domain } from '../../types';
import axiosInstance from '../../lib/api/axiosInstance';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';

interface DomainManagementDialogProps {
  open: boolean;
  onClose: () => void;
  network: Network | null;
}

interface DomainFormData {
  name: string;
  url: string;
}

export const DomainManagementDialog: React.FC<DomainManagementDialogProps> = ({
  open,
  onClose,
  network,
}) => {
  const { t } = useTranslation();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [isAddingDomain, setIsAddingDomain] = useState(false);
  const [editingDomain, setEditingDomain] = useState<Domain | null>(null);
  const [formData, setFormData] = useState<DomainFormData>({
    name: '',
    url: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<DomainFormData>>({});
  const [isLoading, setIsLoading] = useState(false);

  // Récupérer les domaines du réseau depuis l'API
  useEffect(() => {
    const fetchDomains = async () => {
      if (network && open) {
        try {
          setIsLoading(true);
          const response = await axiosInstance.get<Domain[]>(`/domains/network/${network.id}`);
          setDomains(response.data);
        } catch (error) {
          console.error('Error fetching domains:', error);
          // Fallback sur les domaines du réseau si l'API échoue
          setDomains(network.domains || []);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchDomains();
  }, [network, open]);

  const resetForm = () => {
    setFormData({ name: '', url: '' });
    setFormErrors({});
    setEditingDomain(null);
    setIsAddingDomain(false);
  };

  const validateForm = (): boolean => {
    const errors: Partial<DomainFormData> = {};
    
    if (!formData.name.trim()) {
      errors.name = t('domainManagement.errors.nameRequired');
    }
    
    if (!formData.url.trim()) {
      errors.url = t('domainManagement.errors.urlRequired');
    } else if (!isValidUrl(formData.url)) {
      errors.url = t('domainManagement.errors.invalidUrl');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm() || !network) return;

    setIsLoading(true);
    try {
      if (editingDomain) {
        // Appel API pour modifier le domaine
        const response = await axiosInstance.put<Domain>(`/domains/${editingDomain.id}`, {
          name: formData.name,
          url: formData.url,
        });
        const updatedDomains = domains.map(d => 
          d.id === editingDomain.id ? response.data : d
        );
        setDomains(updatedDomains);
      } else {
        // Appel API pour créer le domaine
        const response = await axiosInstance.post<Domain>('/domains', {
          name: formData.name,
          url: formData.url,
          networkId: network.id,
        });
        setDomains([...domains, response.data]);
      }
      resetForm();
    } catch (error) {
      console.error('Error saving domain:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (domain: Domain) => {
    setEditingDomain(domain);
    setFormData({
      name: domain.name,
      url: domain.url,
    });
    setIsAddingDomain(true);
  };

  const handleDelete = async (domain: Domain) => {
    if (window.confirm(t('domainManagement.confirmDelete', { name: domain.name }))) {
      try {
        setIsLoading(true);
        await axiosInstance.delete(`/domains/${domain.id}`);
        setDomains(domains.filter(d => d.id !== domain.id));
      } catch (error) {
        console.error('Error deleting domain:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            {t('domainManagement.title')}
            {network && (
              <Badge variant="outline" className="ml-2">
                {network.name}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {t('domainManagement.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {/* Formulaire d'ajout/modification */}
          {isAddingDomain && (
            <div className="border rounded-lg p-4 mb-4 bg-muted/20">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium">
                  {editingDomain 
                    ? t('domainManagement.editDomain') 
                    : t('domainManagement.addDomain')
                  }
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetForm}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid gap-3">
                <div>
                  <Label htmlFor="domain-name">{t('domainManagement.name')}</Label>
                  <Input
                    id="domain-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('domainManagement.namePlaceholder')}
                  />
                  {formErrors.name && (
                    <span className="text-sm text-destructive">{formErrors.name}</span>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="domain-url">{t('domainManagement.url')}</Label>
                  <Input
                    id="domain-url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder={t('domainManagement.urlPlaceholder')}
                  />
                  {formErrors.url && (
                    <span className="text-sm text-destructive">{formErrors.url}</span>
                  )}
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={resetForm}>
                    {t('common.cancel')}
                  </Button>
                  <Button onClick={handleSubmit} disabled={isLoading}>
                    {isLoading ? t('common.saving') : t('common.save')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Bouton d'ajout */}
          {!isAddingDomain && (
            <div className="mb-4">
              <Button
                onClick={() => setIsAddingDomain(true)}
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('domainManagement.addDomain')}
              </Button>
            </div>
          )}

          {/* Liste des domaines */}
          <div className="flex-1">
            {domains.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Globe className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>{t('domainManagement.noDomains')}</p>
              </div>
            ) : (
              <ScrollArea className="h-[300px]">
                <div className="space-y-2 pr-4">
                  {domains.map((domain) => (
                    <div
                      key={domain.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-border/40 bg-background hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{domain.name}</span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {domain.url}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(domain)}
                          className="h-8 w-8 p-0"
                          disabled={isAddingDomain}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(domain)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                          disabled={isAddingDomain}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>
            {t('common.close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};